import { useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from "react";
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import type { ProductSeriesPoint, VelocityClass } from "../types";

interface RegressionChartProps {
  series: ProductSeriesPoint[];
  forecastSeries: ProductSeriesPoint[];
  productName: string;
  category?: VelocityClass;
  r2?: number;
  slope?: number;
  slopePercent?: number;
}

type TrendTone = "surging" | "rising" | "level" | "cooling" | "falling";

interface TrendReading {
  tone: TrendTone;
  title: string;
  detail: string;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function pathFor(points: Array<{ x: number; y: number }>) {
  return points
    .map((point, index) => `${index ? "L" : "M"} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`)
    .join(" ");
}

function classifyTrend(slopePercent: number, r2: number): TrendReading {
  let tone: TrendTone;
  if (slopePercent >= 4) tone = "surging";
  else if (slopePercent >= 1) tone = "rising";
  else if (slopePercent <= -4) tone = "falling";
  else if (slopePercent <= -1) tone = "cooling";
  else tone = "level";

  const readings: Record<TrendTone, Omit<TrendReading, "tone">> = {
    surging: { title: "强势上扬", detail: "阻尼趋势与近期出库同时抬升，预测区间随周期项向上展开。" },
    rising: { title: "温和上扬", detail: "出库量逐日抬升，建议保持短周期滚动复盘。" },
    level: { title: "横盘稳定", detail: "趋势项接近持平，补货以周期波动和库存覆盖为主。" },
    cooling: { title: "温和回落", detail: "出库量正在走弱，应缩短观察窗口并控制近端补货。" },
    falling: { title: "明显回落", detail: "下降趋势延续，需防止近端库位和后续到货积压。" },
  };
  const reading = readings[tone];
  return {
    tone,
    ...reading,
    detail:
      r2 < 0.32 && (tone === "surging" || tone === "falling")
        ? `${reading.detail} R² 偏低，方向仅作辅助信号。`
        : reading.detail,
  };
}

export function RegressionChart({
  series,
  forecastSeries,
  productName,
  category,
  r2 = 0,
  slope = 0,
  slopePercent = 0,
}: RegressionChartProps) {
  const chartRef = useRef<SVGSVGElement>(null);
  const scrubbingRef = useRef(false);
  const allPoints = useMemo(() => [...series, ...forecastSeries], [series, forecastSeries]);
  const actualCount = series.length;
  const [activeIndex, setActiveIndex] = useState(Math.max(0, actualCount - 1));
  const average = series.length
    ? series.reduce((sum, point) => sum + point.quantity, 0) / series.length
    : 0;
  const effectiveSlopePercent = slopePercent || (average ? (slope / average) * 100 : 0);
  const trend = classifyTrend(effectiveSlopePercent, r2);
  const active = allPoints[activeIndex] ?? allPoints.at(-1);
  const isFuture = active?.dateKind === "forecast";
  const activeValue = isFuture ? active?.forecast ?? 0 : active?.quantity ?? 0;
  const width = 980;
  const height = 410;
  const margin = { top: 64, right: 118, bottom: 56, left: 62 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;
  const values = [
    ...series.flatMap((point) => [point.quantity, point.fitted ?? point.quantity]),
    ...forecastSeries.flatMap((point) => [point.forecast ?? 0, point.lower ?? 0, point.upper ?? 0]),
  ];
  const maxValue = Math.max(...values, 1);
  const minValue = Math.min(...values, 0);
  const yMax = Math.ceil((maxValue * 1.12) / 10) * 10;
  const yMin = Math.max(0, Math.floor((minValue * 0.82) / 10) * 10);
  const xAt = (index: number) =>
    margin.left + (allPoints.length <= 1 ? plotWidth / 2 : (index / (allPoints.length - 1)) * plotWidth);
  const yAt = (value: number) =>
    margin.top + plotHeight - ((value - yMin) / Math.max(1, yMax - yMin)) * plotHeight;

  const actualPoints = series.map((point, index) => ({ x: xAt(index), y: yAt(point.quantity) }));
  const fittedPoints = series.map((point, index) => ({
    x: xAt(index),
    y: yAt(point.fitted ?? point.quantity),
  }));
  const forecastOrigin = actualPoints.at(-1) ?? { x: margin.left, y: yAt(0) };
  const forecastPoints = forecastSeries.map((point, index) => ({
    x: xAt(actualCount + index),
    y: yAt(point.forecast ?? 0),
  }));
  const futureLinePoints = [forecastOrigin, ...forecastPoints];
  const upperPoints = forecastSeries.map((point, index) => ({
    x: xAt(actualCount + index),
    y: yAt(point.upper ?? point.forecast ?? 0),
  }));
  const lowerPoints = forecastSeries.map((point, index) => ({
    x: xAt(actualCount + index),
    y: yAt(point.lower ?? point.forecast ?? 0),
  }));
  const actualPath = pathFor(actualPoints);
  const fittedPath = pathFor(fittedPoints);
  const futurePath = pathFor(futureLinePoints);
  const areaPath = actualPoints.length
    ? `${actualPath} L ${actualPoints.at(-1)!.x} ${yAt(yMin)} L ${actualPoints[0].x} ${yAt(yMin)} Z`
    : "";
  const forecastBandPath = upperPoints.length
    ? [
        `M ${forecastOrigin.x} ${forecastOrigin.y}`,
        ...upperPoints.map((point) => `L ${point.x} ${point.y}`),
        ...[...lowerPoints].reverse().map((point) => `L ${point.x} ${point.y}`),
        "Z",
      ].join(" ")
    : "";
  const labelStep = Math.max(1, Math.ceil(allPoints.length / 9));
  const ticks = Array.from({ length: 5 }, (_, index) => yMin + ((yMax - yMin) * index) / 4);
  const boundaryX = actualCount ? xAt(actualCount - 1) : margin.left;

  useEffect(() => {
    setActiveIndex(Math.max(0, actualCount - 1));
  }, [series, actualCount]);

  const indexFromClientX = (clientX: number) => {
    const bounds = chartRef.current?.getBoundingClientRect();
    if (!bounds || allPoints.length <= 1) return 0;
    const svgX = ((clientX - bounds.left) / bounds.width) * width;
    const ratio = (svgX - margin.left) / Math.max(1, plotWidth);
    return clamp(Math.round(ratio * (allPoints.length - 1)), 0, allPoints.length - 1);
  };

  const scrub = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (!allPoints.length) return;
    setActiveIndex(indexFromClientX(event.clientX));
  };

  const startScrub = (event: ReactPointerEvent<SVGSVGElement>) => {
    scrubbingRef.current = true;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    scrub(event);
  };

  const stopScrub = (event: ReactPointerEvent<SVGSVGElement>) => {
    scrubbingRef.current = false;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const moveScrub = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (scrubbingRef.current) scrub(event);
  };

  const keyboardScrub = (event: ReactKeyboardEvent<SVGSVGElement>) => {
    if (!allPoints.length) return;
    const lastIndex = allPoints.length - 1;
    if (event.key === "ArrowLeft" || event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => clamp(index - 1, 0, lastIndex));
    } else if (event.key === "ArrowRight" || event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => clamp(index + 1, 0, lastIndex));
    } else if (event.key === "Home") {
      event.preventDefault();
      setActiveIndex(0);
    } else if (event.key === "End") {
      event.preventDefault();
      setActiveIndex(lastIndex);
    }
  };

  const TrendIcon =
    trend.tone === "falling" || trend.tone === "cooling"
      ? ArrowDownRight
      : trend.tone === "level"
        ? Minus
        : ArrowUpRight;
  const tooltipWidth = 166;
  const tooltipHeight = active?.shock ? 82 : 76;

  return (
    <div className={`regression-chart ${allPoints.length ? "" : "is-empty"}`}>
      <div className="chart-reading-bar" aria-live="polite">
        <div className={`chart-trend-signal ${trend.tone}`}>
          <TrendIcon size={18} aria-hidden="true" />
          <span>
            <small>趋势判定</small>
            <strong>{trend.title}</strong>
          </span>
        </div>
        <div className="chart-reading-evidence">
          <span><b>{effectiveSlopePercent >= 0 ? "+" : ""}{effectiveSlopePercent.toFixed(1)}%</b> 斜率 / 日均</span>
          <span><b>{r2.toFixed(2)}</b> 拟合 R²</span>
          {category && <span className="chart-category-reading"><b>{category}</b> 综合分类</span>}
        </div>
        <p>{trend.detail}</p>
      </div>

      <div className="chart-legend">
        <span><i className="legend-line actual" />实际出库</span>
        <span><i className="legend-line fitted" />Holt 拟合</span>
        <span><i className="legend-line forecast" />未来预测</span>
        <span><i className="legend-band" />90% 区间</span>
        <span><i className="legend-shock" />异常脉冲</span>
        <span className="chart-drag-hint">拖动或使用 ← → 逐日查看</span>
        <span className="chart-product-name">{productName}</span>
      </div>

      <svg
        ref={chartRef}
        viewBox={`0 0 ${width} ${height}`}
        role="slider"
        tabIndex={0}
        aria-label={`${productName} 实际出库、Holt 拟合及未来 14 日预测`}
        aria-valuemin={1}
        aria-valuemax={Math.max(1, allPoints.length)}
        aria-valuenow={activeIndex + 1}
        aria-valuetext={
          active
            ? isFuture
              ? `${active.label}，预测 ${activeValue.toFixed(1)} 件，90% 区间 ${active.lower?.toFixed(1) ?? "0"} 至 ${active.upper?.toFixed(1) ?? "0"} 件`
              : `${active.label}，实际出库 ${activeValue} 件，Holt 拟合 ${active.fitted?.toFixed(1) ?? "0"} 件`
            : "暂无趋势数据"
        }
        aria-describedby="regression-chart-help"
        onPointerDown={startScrub}
        onPointerMove={moveScrub}
        onPointerUp={stopScrub}
        onPointerCancel={stopScrub}
        onKeyDown={keyboardScrub}
      >
        <defs>
          <linearGradient id="actual-area" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#dfc990" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#dfc990" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="fitted-wash" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#59d7ac" stopOpacity=".2" />
            <stop offset="100%" stopColor="#59d7ac" stopOpacity=".86" />
          </linearGradient>
          <linearGradient id="forecast-band" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#72c7eb" stopOpacity=".2" />
            <stop offset="100%" stopColor="#72c7eb" stopOpacity=".04" />
          </linearGradient>
          <filter id="point-glow" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {ticks.map((tick) => (
          <g key={tick}>
            <line x1={margin.left} x2={width - margin.right} y1={yAt(tick)} y2={yAt(tick)} className="chart-gridline" />
            <text x={margin.left - 14} y={yAt(tick) + 4} textAnchor="end" className="chart-axis-label">
              {Math.round(tick)}
            </text>
          </g>
        ))}

        {forecastSeries.length > 0 && (
          <>
            <line x1={boundaryX} x2={boundaryX} y1={margin.top} y2={margin.top + plotHeight} className="chart-forecast-boundary" />
            <text x={boundaryX + 9} y={margin.top + 12} className="chart-forecast-caption">预测起点</text>
            <path d={forecastBandPath} className="chart-forecast-band" />
            <path d={futurePath} className="chart-forecast-line" />
          </>
        )}
        <path d={areaPath} fill="url(#actual-area)" />
        <path d={actualPath} className="chart-actual-line" />
        <path d={fittedPath} className="chart-regression-line" />

        {series.map((point, index) => (
          <g key={`actual-${point.date}`}>
            {point.shock && <circle cx={xAt(index)} cy={yAt(point.quantity)} r={8} className="chart-shock-ring" aria-hidden="true" />}
            <circle
              cx={xAt(index)}
              cy={yAt(point.quantity)}
              r={activeIndex === index ? 5.5 : point.shock ? 4 : 3}
              className={`chart-point ${activeIndex === index ? "is-active" : ""} ${point.shock ? "is-shock" : ""}`}
              onPointerEnter={() => {
                if (!scrubbingRef.current) setActiveIndex(index);
              }}
              aria-hidden="true"
            />
          </g>
        ))}

        {forecastSeries.map((point, index) => {
          const combinedIndex = actualCount + index;
          return (
            <rect
              key={`forecast-${point.date}`}
              x={xAt(combinedIndex) - 3.2}
              y={yAt(point.forecast ?? 0) - 3.2}
              width={6.4}
              height={6.4}
              transform={`rotate(45 ${xAt(combinedIndex)} ${yAt(point.forecast ?? 0)})`}
              className={`chart-forecast-point ${activeIndex === combinedIndex ? "is-active" : ""}`}
              onPointerEnter={() => {
                if (!scrubbingRef.current) setActiveIndex(combinedIndex);
              }}
              aria-hidden="true"
            />
          );
        })}

        {allPoints.map((point, index) =>
          index % labelStep === 0 || index === allPoints.length - 1 ? (
            <text
              key={`label-${point.date}`}
              x={xAt(index)}
              y={height - 25}
              textAnchor="middle"
              className={`chart-axis-label ${point.dateKind === "forecast" ? "is-forecast" : ""}`}
            >
              {point.label}
            </text>
          ) : null,
        )}

        {active && (
          <g className="chart-tooltip" pointerEvents="none">
            <line x1={xAt(activeIndex)} x2={xAt(activeIndex)} y1={margin.top} y2={margin.top + plotHeight} className="chart-crosshair" />
            <circle
              cx={xAt(activeIndex)}
              cy={yAt(activeValue)}
              r={7}
              className={`chart-point-halo ${isFuture ? "is-forecast" : ""}`}
              filter="url(#point-glow)"
            />
            <g transform={`translate(${clamp(xAt(activeIndex) - tooltipWidth / 2, 72, width - margin.right - tooltipWidth - 6)}, ${Math.max(14, yAt(activeValue) - tooltipHeight - 22)})`}>
              <rect width={tooltipWidth} height={tooltipHeight} rx="5" className="chart-tooltip-bg" />
              <text x="12" y="21" className="chart-tooltip-date">{active.label} · {isFuture ? "预测" : "实际"}</text>
              <text x="12" y="40" className="chart-tooltip-value">
                {isFuture ? `预测 ${active.forecast?.toFixed(1)} 件` : `实际 ${active.quantity} 件`}
              </text>
              {isFuture ? (
                <text x="12" y="58" className="chart-tooltip-range">
                  90% 区间 {active.lower?.toFixed(1)}–{active.upper?.toFixed(1)} 件
                </text>
              ) : (
                <text x="12" y="58" className="chart-tooltip-fitted">
                  Holt 拟合 {active.fitted?.toFixed(1)} 件
                </text>
              )}
              {active.shock && <text x="12" y="72" className="chart-tooltip-shock">异常脉冲 · 已稳健降权</text>}
            </g>
          </g>
        )}
        <rect x={margin.left} y={margin.top} width={plotWidth} height={plotHeight} fill="transparent" className="chart-scrub-surface" />
      </svg>

      <div className="chart-live-readout" aria-live="polite">
        <span><small>当前日期</small><strong>{active?.label ?? "暂无数据"}</strong></span>
        <span><small>{isFuture ? "预测出库" : "实际出库"}</small><strong>{activeValue.toFixed(isFuture ? 1 : 0)} 件</strong></span>
        <span>
          <small>{isFuture ? "90% 区间" : "Holt 拟合"}</small>
          <strong>
            {isFuture
              ? `${active?.lower?.toFixed(0) ?? "0"}–${active?.upper?.toFixed(0) ?? "0"} 件`
              : `${active?.fitted?.toFixed(1) ?? "0.0"} 件`}
          </strong>
        </span>
        <span><small>异常脉冲</small><strong>{series.filter((point) => point.shock).length} 天</strong></span>
      </div>
      <p id="regression-chart-help" className="chart-a11y-help">
        实线为实际出库，绿色曲线为阻尼 Holt 与周期项拟合，蓝色虚线与渐变带展示未来 14 日预测和 90% 区间。
        异常脉冲会被稳健残差降权，避免单日突增或骤降带偏补货量。
      </p>
    </div>
  );
}
